import React from 'react';
import PropTypes from 'prop-types';

// material
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

//style
import { useStyles } from './style';

export default function Form({
    title,
    open,
    onClose,
    onSubmit,
    slotEnum,
    dateEnum,
}) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [slot, setSlot] = React.useState('');
    const [date, setDate] = React.useState('');
    const [error, setError] = React.useState({ slot: false, date: false });

    const handleSlotChange = (event) => {
        const value = event.target.value;
        setSlot(event.target.value);
        setError({ ...error, slot: !value });
    };

    const handleDateChange = (event) => {
        const value = event.target.value;
        setDate(event.target.value);
        setError({ ...error, date: !value });
    };

    const handleClose = () => {
        onClose();
    };

    const handleFormSubmit = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (!(slot && date)) {
            setError({ slot: !slot, date: !date });
            return;
        }

        onSubmit({ slot, eventStartDate: date });
        handleClose();
    };

    const renderForm = () => {
        return (
            <form onSubmit={handleFormSubmit}>
                <Box display="flex" flexDirection={isMobile ? "column" : "row"} gridGap={isMobile ? 24 : 8}>
                    <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={error.date}
                    >
                        <InputLabel id="date" className={classes.label}>Select date</InputLabel>
                        <Select
                            labelId="date"
                            id="date"
                            value={date}
                            onChange={handleDateChange}
                            label="Select date"
                            classes={{ select: `${classes.select} ${classes.label}` }}
                            MenuProps={{ classes: { paper: classes.selectMenuPaper } }}
                        >
                            {dateEnum.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    value={item}
                                    classes={{ root: classes.label }}
                                >
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.date && <FormHelperText>{'Select a date'}</FormHelperText>}
                    </FormControl>
                    <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={error.slot}
                    >
                        <InputLabel id="slot" className={classes.label}>Pick a slot</InputLabel>
                        <Select
                            labelId="slot"
                            id="slot"
                            value={slot}
                            onChange={handleSlotChange}
                            label="Pick a slot"
                            classes={{ select: `${classes.select} ${classes.label}` }}
                            MenuProps={{ classes: { paper: classes.selectMenuPaper } }}
                        >
                            {slotEnum.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    value={item}
                                    classes={{ root: classes.label }}
                                >
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.slot && <FormHelperText>{'Select a slot'}</FormHelperText>}
                    </FormControl>
                </Box>
                <div className={classes.actionContainer}>
                    <Button type="submit" variant="outlined" color="inherit" fullWidth>
                        Register
                    </Button>
                </div>
            </form>
        );
    };

    if (isMobile) {
        return (
            <Drawer
                open={open}
                onClose={handleClose}
                classes={{
                    paper: classes.drawerPaper,
                    root: classes.drawerRoot,
                }}
                anchor="right"
            >
                {title && (
                    <Box className={`${classes.titleWrapper} ${classes.drawerTitle}`}>
                        <Typography className={classes.titleHeading}>{title}</Typography>
                        <IconButton
                            color="default"
                            variant="normal"
                            size="small"
                            onClick={handleClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
                <Box className={classes.drawerContent}>
                    {renderForm()}
                </Box>
            </Drawer>
        );
    }

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            transitionDuration={250}
        >
            {title && (
                <DialogTitle disableTypography className={classes.titleWrapper}>
                    <Typography className={classes.titleHeading}>{title}</Typography>
                    <IconButton
                        color="default"
                        variant="normal"
                        size="small"
                        onClick={handleClose}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
            )}
            <DialogContent>{renderForm()}</DialogContent>
        </Dialog>
    );
}

Form.propTypes = {
    title: PropTypes.string.isRequired,
    slotEnum: PropTypes.array,
    dateEnum: PropTypes.array,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

Form.defaultProps = {
    open: false,
};
